import React, { useEffect, useRef } from "react";
import { useTheme } from "next-themes";
import { routeColorFor } from "@/lib/mapStyle";

/**
 * Renders a run route as an SVG overlay on top of a MapLibre base map.
 *
 * Instead of relying on MapLibre GL "line"/"circle" layers (which depend on the
 * WebGL vector pipeline + style being fully loaded), this projects each
 * [lng, lat] coordinate to screen pixels via `map.project()` on the map's
 * `render` / `move` / `resize` events and draws:
 *   - a blurred neon "glow" polyline underlay
 *   - a solid neon polyline for the route
 *   - optional start (neon) / end (white) circle markers
 *
 * DOM attributes are updated imperatively inside the event handler for
 * performance (no React re-render per animation frame). This works as soon as
 * the map has a valid transform — no need to wait for the style `load` event.
 */
export const RouteSvgOverlay = ({
  map,
  coordinates = [],
  showEndpoints = false,
  lineWidth = 5,
  color,
  zIndex = 2,
}) => {
  const { resolvedTheme } = useTheme();
  // Explicit `color` prop wins; otherwise pick a theme-appropriate route color.
  const routeColor = color || routeColorFor(resolvedTheme);
  const svgRef = useRef(null);
  const glowRef = useRef(null);
  const lineRef = useRef(null);
  const startRef = useRef(null);
  const endRef = useRef(null);
  // Keep latest coords in a ref so the render/move handler always reads fresh data
  const coordsRef = useRef(coordinates);
  coordsRef.current = coordinates;

  const sync = () => {
    if (!map || !svgRef.current) return;
    const coords = coordsRef.current || [];
    const projected = [];
    try {
      for (const c of coords) {
        if (!c || c.length < 2) continue;
        const p = map.project(c);
        if (p && Number.isFinite(p.x) && Number.isFinite(p.y)) projected.push(p);
      }
    } catch (e) {
      return;
    }
    const pts = projected.map((p) => `${p.x},${p.y}`).join(" ");

    if (projected.length >= 2) {
      if (glowRef.current) { glowRef.current.setAttribute("points", pts); glowRef.current.style.display = ""; }
      if (lineRef.current) { lineRef.current.setAttribute("points", pts); lineRef.current.style.display = ""; }
    } else {
      if (glowRef.current) glowRef.current.style.display = "none";
      if (lineRef.current) lineRef.current.style.display = "none";
    }

    if (showEndpoints && projected.length >= 1) {
      const s = projected[0];
      const e = projected[projected.length - 1];
      if (startRef.current) { startRef.current.setAttribute("cx", s.x); startRef.current.setAttribute("cy", s.y); startRef.current.style.display = ""; }
      if (endRef.current) { endRef.current.setAttribute("cx", e.x); endRef.current.setAttribute("cy", e.y); endRef.current.style.display = ""; }
    }
  };

  // Bind to map events so the overlay stays aligned during pan / zoom / animations.
  useEffect(() => {
    if (!map) return undefined;
    const onSync = () => sync();
    map.on("render", onSync);
    map.on("move", onSync);
    map.on("resize", onSync);
    onSync();
    return () => {
      try {
        map.off("render", onSync);
        map.off("move", onSync);
        map.off("resize", onSync);
      } catch (e) { /* map may already be removed */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  // Re-sync immediately whenever the coordinates change (e.g. new GPS fix).
  useEffect(() => {
    sync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coordinates]);

  return (
    <svg
      ref={svgRef}
      width="100%"
      height="100%"
      style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex }}
      data-testid="route-svg-overlay"
    >
      <polyline ref={glowRef} fill="none" stroke={routeColor} strokeOpacity="0.5" strokeWidth={lineWidth + 9} strokeLinejoin="round" strokeLinecap="round" style={{ filter: "blur(4px)", display: "none" }} />
      <polyline ref={lineRef} fill="none" stroke={routeColor} strokeWidth={lineWidth} strokeLinejoin="round" strokeLinecap="round" style={{ display: "none" }} />
      {showEndpoints && (
        <>
          <circle ref={startRef} r="6" fill={routeColor} stroke="#0b0b0c" strokeWidth="2" style={{ display: "none" }} />
          <circle ref={endRef} r="6" fill="#ffffff" stroke="#0b0b0c" strokeWidth="2" style={{ display: "none" }} />
        </>
      )}
    </svg>
  );
};
